import { Component, Input, OnChanges, OnDestroy, OnInit, inject, input } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { AnchorDirective, CurrencyPipe, DynamicHtmlDirective, NavigationService } from '@frontend/vanilla/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AppConstants, Config, InputType, LIMIT_PERIOD } from '../../../config/appConstants';
import { DepositInfo } from '../../../model/depositInfo.model';
import { MessageType } from '../../../model/message.model';
import { QDInstrument, TxnLimitInfo } from '../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../service/behaviorData.service';
import { DepositService } from '../../../service/deposit.service';
import { EventService } from '../../../service/event.service';
import { GtmService } from '../../../service/gtm.service';
import { OptionListService } from '../../../service/optionList.service';
import { UserDataService } from '../../../service/userDataService';
import { MessageComponent } from '../../../shared/components/messages/message-component';

@Component({
    selector: 'app-deposit-limit-message',
    templateUrl: 'deposit-limit-message.component.html',
    imports: [MessageComponent, TranslateModule, CurrencyPipe, DynamicHtmlDirective, AnchorDirective],
})
export class DepositLimitMessageComponent implements OnInit, OnChanges, OnDestroy {
    private optionListService = inject(OptionListService);
    behaviorData = inject(BehaviorDataService);
    private depositService = inject(DepositService);
    userDataService = inject(UserDataService);
    private translate = inject(TranslateService);
    private GTMService = inject(GtmService);
    private navigationService = inject(NavigationService);
    private eventService = inject(EventService);

    readonly selectedOption = input.required<QDInstrument>();
    @Input() amountFormGroup: FormGroup;
    depositInfo: DepositInfo;
    selfLimits: TxnLimitInfo;

    maxDepositLimit: any;
    ccDepositLimitCheckEnabled: boolean;
    showCCDepositLimitCheckMsg: boolean;
    ccCurrentDepositLimit: string;
    limitPeriod: string;
    country: string;
    maxLimit: number;
    $subscriptions: Subscription[] = [];
    minDepositLimitCheck: boolean;
    enablePrepaidcardRestrictions: boolean;
    rollingWeekMaxAmount: number;
    showPrepaidCardsExceedsMessage: boolean = false;
    rollingWeekRemainingAmount: number;
    instrumentName: any;
    currencyUSD: string = 'USD';
    spainRekycLimitTemplate: string = '';
    currencyEUR: any;
    es_threshold_limit: number;
    enteredAmount: string = '0';
    svocLimit: number;
    findoutMoreLink: string;
    // showinlineMsg: boolean;
    public get messageType(): typeof MessageType {
        return MessageType;
    }

    constructor() {
        this.depositInfo = this.depositService?.depositInfo;
        this.ccDepositLimitCheckEnabled = this.userDataService?.userData?.configParams?.isCreditCardDepositLimitCheckEnabled === Config.YES;
        this.ccCurrentDepositLimit = this.userDataService?.userData?.configParams?.currentCreditCardDepositLimit;
        this.country = this.userDataService?.userData?.userInfo?.addressInfo?.country;
        this.enablePrepaidcardRestrictions = this.userDataService?.userData?.configParams?.enable_prepaidcard_restrictions == 'Y' ? true : false;
        this.rollingWeekRemainingAmount = Number(this.userDataService?.userData?.configParams?.rollingweek_remaining_balance);
        this.rollingWeekMaxAmount = Number(this.userDataService?.userData?.configParams?.RollingWeek_MaxAmount);
        this.svocLimit = Number(this.userDataService?.userData?.configParams?.svoc_daily_deposit_limit);
        this.findoutMoreLink = this.userDataService?.userData?.configParams?.svoc_findoutmore_details;
    }

    ngOnInit(): void {
        this.initialize();
    }

    ngOnChanges() {
        this.initialize();
    }

    initialize() {
        if (this.selectedOption()?.paymentMethodInfo) {
            this.selfLimits = this.selectedOption().paymentMethodInfo?.txnLimitInfo;
            this.maxLimit = this.selectedOption().paymentMethodInfo?.maxDepositLimit?.value;
            this.limitPeriod = this.selfLimits?.type.substring(this.selfLimits?.type.lastIndexOf('_') + 1);
            this.limitPeriod = this.limitPeriod ? this.limitPeriod[0]?.toUpperCase() + this.limitPeriod?.slice(1) : '';

            const maxLimit = this.selectedOption().paymentMethodInfo?.txnLimitInfo?.maxLimit?.value;
            this.maxDepositLimit = maxLimit ? parseFloat(maxLimit).toFixed(2) : 0;
            if (LIMIT_PERIOD.includes(this.limitPeriod) && this.selfLimits?.type?.includes('purchase_limit')) {
                this.limitPeriod = this.translate.instant('PARTY.CASHIER.web.deposit.purchase.limit.' + this.limitPeriod.toLowerCase());
                this.maxDepositLimit = Number(this.maxDepositLimit);
            }
            this.minDepositLimitCheck =
                parseFloat(this.maxDepositLimit) > this.selectedOption().paymentMethodInfo?.minDepositLimit?.value ? true : false;
            if (this.depositInfo?.isBelgiumRgDecreeEnabled) {
                this.maxLimit = parseFloat(this.selectedOption().paymentMethodInfo?.txnLimitInfo?.maxLimit?.value);
            }
            const paymentMethod = this.selectedOption().paymentMethodInfo?.paymentMethod;
            this.instrumentName = paymentMethod[0]?.toUpperCase() + paymentMethod?.toLowerCase().slice(1, paymentMethod?.length);
            if (paymentMethod?.toUpperCase() === InputType.INSTANTECHECKS) {
                this.instrumentName = this.translate.instant('PARTY.CASHIER.web.deposit.instantechecks.name.us');
            } else if (paymentMethod?.toUpperCase() === InputType.BORGATAPREPAIDCARD) {
                this.instrumentName = this.translate.instant('PARTY.CASHIER.web.deposit.ewallet.play.name.us');
            } else if (paymentMethod?.toUpperCase() === InputType.PLAYMGMPREPAIDCARD) {
                this.instrumentName = this.translate.instant('PARTY.CASHIER.web.deposit.ewallet.betmgmplay.name.us');
            } else if (paymentMethod?.toUpperCase() === InputType.MLIFE) {
                this.instrumentName = this.translate.instant('PARTY.CASHIER.web.deposit.mgmrewards.name');
            }
            if (this.amountFormGroup?.controls?.userAmount?.hasError('spainOdInlineVal')) {
                this.currencyEUR = this.userDataService?.userData?.userInfo?.accountCurrency;
                this.GTMService.raiseEventForODbannerLoad(
                    this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod.toLowerCase() + ' - under 2k',
                );
            }
        }
        this.es_threshold_limit = Number(this.selectedOption()?.paymentAccountInfo?.es_threshold_limit);

        this.$subscriptions.push(
            this.behaviorData.getCCDepositLimitCheckMsg.subscribe((data) => {
                this.showCCDepositLimitCheckMsg = data;
            }),
        );
        if (this.enablePrepaidcardRestrictions) {
            this.$subscriptions.push(
                this.behaviorData.getEnteredAmount.subscribe((data) => {
                    if (data && this.selectedOption()?.fundingSource?.toLowerCase() == AppConstants.PREPAID) {
                        if (
                            !this.amountFormGroup?.controls?.userAmount?.hasError('selfSetLimit') ||
                            (this.amountFormGroup?.controls?.userAmount?.hasError('selfSetLimit') &&
                                Number(this.maxDepositLimit) > Number(this.rollingWeekRemainingAmount))
                        ) {
                            this.showPrepaidCardsExceedsMessage = Number(data) > Number(this.rollingWeekRemainingAmount) ? true : false;
                        }
                    }
                }),
            );
        }
        this.$subscriptions.push(
            this.behaviorData.getEnteredAmount.subscribe((data) => {
                if (
                    data &&
                    (this.amountFormGroup?.controls?.userAmount?.hasError('spainRekycLimit') ||
                        this.amountFormGroup?.controls?.userAmount?.hasError('spainAnnRekycLimitExceeded') ||
                        this.amountFormGroup?.controls?.userAmount?.hasError('expiredKycLimitValidation'))
                ) {
                    if (
                        Number(this.userDataService?.userData?.configParams?.rekycRemainingLimit) <
                        Number(this.selectedOption()?.paymentMethodInfo?.minDepositLimit?.value)
                    ) {
                        this.spainRekycLimitTemplate = 'unAvailable';
                    } else {
                        this.spainRekycLimitTemplate = 'available';
                    }
                }
                if (this.enteredAmount != data && this.selfLimits?.type?.includes('svoc_limit')) {
                    const isLimitExceeded =
                        Number(this.maxDepositLimit) > 0 && this.amountFormGroup?.controls?.userAmount?.value > Number(this.maxDepositLimit);
                    const isLimitReached = Number(this.maxDepositLimit) === 0;

                    if (isLimitExceeded || isLimitReached) {
                        if (this.userDataService?.userData?.configParams?.svoc_dailydepositcap_attempttoexceed === 'N') {
                            this.depositService.triggerExceededSVOCAlert(this.maxDepositLimit, this.amountFormGroup?.controls?.userAmount?.value);
                        }
                        if (this.userDataService?.userData?.configParams?.svoc_dailydepositcap_reached === 'N') {
                            setTimeout(() => {
                                this.depositService.triggerExceededSVOCAlert(this.maxDepositLimit, this.amountFormGroup?.controls?.userAmount?.value);
                            }, 100);
                        }
                        const errorType = this.maxDepositLimit > 0 ? 'deposit svoc limit to be exceeded' : 'deposit svoc limit exceeded';
                        this.GTMService.raiseEventForSVOCValidations(errorType, this.selectedOption().paymentMethodInfo?.paymentMethod);
                    }
                }
                this.enteredAmount = data;
            }),
        );
    }
    updateAmount(feat: any): void {
        if (feat === 'occupDet') {
            this.behaviorData.changeEnteredAmount(this.behaviorData?.userODetails?.allowedAmt);
            this.GTMService.raiseEventForODbannerClicks(
                'click',
                this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod.toLowerCase() + ' - under 2k',
                'update amount',
                'not applicable',
            );
        } else if (feat === 'amlThresh') {
            this.GTMService.raiseEventForInsVerClick(
                this.selectedOption()?.paymentMethodInfo?.paymentMethod + ' - under 2k banner',
                'update amount',
                'not applicable',
            );
            this.behaviorData.changeEnteredAmount(this.selectedOption()?.paymentAccountInfo?.es_allowed_amount ?? '');
        } else if (feat === 'annRekyc') {
            const bannerName = 'you can deposit upto ' + this.userDataService?.userData?.configParams?.rekycRemainingLimit + '€ banner';
            this.GTMService.raiseEventForAnnRekycClick(bannerName + ' - update amount cta', 'not applicable');
            this.behaviorData.changeEnteredAmount(this.userDataService?.userData?.configParams?.rekycRemainingLimit);
        }
    }

    uploadMyIdAnn(isLimitAvailable: boolean) {
        const bannerName = isLimitAvailable
            ? 'you can deposit upto ' + this.userDataService?.userData?.configParams?.rekycRemainingLimit + '€ banner'
            : 'payment method restriction banner';
        if (this.amountFormGroup?.controls?.userAmount?.hasError('expiredKycLimitValidation')) {
            this.GTMService.raiseEventForEsExpIdKycClick(
                bannerName + ' - update amount cta',
                this.userDataService?.userData?.configParams?.rekycUrl + '&cashierQd=true',
                'partially verified/web verification passed',
            );
        } else {
            this.GTMService.raiseEventForAnnRekycClick(bannerName + ' - upload my id cta', this.userDataService?.userData?.configParams?.rekycUrl);
        }
    }

    verifyAccount(option: any, bannerName: any) {
        const urlTemp =
            option?.paymentAccountInfo?.es_jumio_verification_url?.replace('{paymentMethodName}', option?.paymentMethodInfo?.paymentMethod) +
            '&cashierQd=true';
        this.GTMService.raiseEventForInsVerClick(option?.paymentMethodInfo?.paymentMethod + ' - ' + bannerName, 'verify', urlTemp);
        this.navigationService.goTo(urlTemp, { forceReloadTarget: 'top' });
    }

    shareOD() {
        const paymentMethod = this.optionListService?.selectedOption?.paymentMethodInfo?.paymentMethod;
        this.GTMService.raiseEventForODbannerClicks(
            'click',
            `${paymentMethod?.toLowerCase()} - under 2k`,
            'share details',
            this.behaviorData?.userODetails?.urlInfo?.url,
        );
    }

    changeAmount(maxAmount: any): void {
        if (this.amountFormGroup?.controls?.userAmount?.hasError('spainRekycLimit')) {
            this.GTMService.raiseEventForRekycClick('click', 'user exceeded deposit limit banner', 'update amount', 'not applicable');
        }
        if (this.amountFormGroup?.controls?.userAmount?.hasError('expiredKycLimitValidation')) {
            const bannerName = 'you can deposit upto ' + this.userDataService?.userData?.configParams?.rekycRemainingLimit + '€ banner';
            this.GTMService.raiseEventForEsExpIdKycClick(
                bannerName + ' - update amount cta',
                'not applicable',
                'partially verified/web verification passed',
            );
        }
        if (this.selfLimits?.type?.includes('svoc_limit')) {
            this.GTMService.raiseEventForSVOCDepositCapLinks(
                'deposit svoc limit to be exceeded',
                'update amount',
                'not applicable',
                this.selectedOption().paymentMethodInfo?.paymentMethod,
            );
        }
        this.behaviorData.changeEnteredAmount(maxAmount);
    }

    updateCcdLimit(): void {
        this.behaviorData.changeEnteredAmount(this.ccCurrentDepositLimit);
    }
    gotoTransactionHistory() {
        this.optionListService.redirectToMainCashier('&type=transaction-history');
    }
    uploadMyId() {
        this.GTMService.raiseEventForRekycClick(
            'click',
            'user exceeded deposit limit banner',
            'upload my id',
            this.userDataService?.userData?.configParams?.rekycUrl,
        );
    }
    findOutMoreClick() {
        this.GTMService.raiseEventForSVOCDepositCapLinks(
            'deposit svoc limit exceeded',
            'find out more ',
            this.userDataService?.userData?.configParams?.svoc_findoutmore_details,
            this.selectedOption().paymentMethodInfo?.paymentMethod,
        );
        this.eventService.raiseCloseEvent();
    }
    ngOnDestroy(): void {
        this.$subscriptions.map((subscription) => subscription.unsubscribe());
    }
}
